import { NgClass } from "@angular/common";
import { AfterViewInit, Component, DoCheck, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, inject } from "@angular/core";
import { AbstractControl, ControlContainer, FormControl, NgModelGroup } from "@angular/forms";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";
import { ClassDirective } from "@ngbracket/ngx-layout/extended";
import { LayoutAlignDirective, LayoutDirective } from "@ngbracket/ngx-layout/flex";
import { FileService } from "@tylertech/core-module";
import { ForgeButtonModule, ForgeDividerModule, ForgeFilePickerModule, ForgeIconButtonModule, ForgeIconModule, ForgeLabelValueModule, ForgeTooltipModule } from "@tylertech/forge-angular";
import { Utilities } from "@tylertech/shared-module";
import { IFile, PropertyMetadata } from "@tylertech/tyler-api-types";
import { AugmentedModelObject, ValueChangedParms } from "../../../../interfaces";
import { MetadataPropertyService } from "../../../../services";
import { ObjectHelpers } from "../../../../utilities";

class BadFile
{
    FileName: string;

    Message: string;
}
@Component({
    selector: "metadata-input-file",
    templateUrl: "./metadata-input-file.tmpl.html",
    viewProviders: [{ provide: ControlContainer, useExisting: NgModelGroup }],
    imports: [
        ClassDirective,
        LayoutAlignDirective,
        LayoutDirective,
        ForgeButtonModule,
        ForgeDividerModule,
        ForgeFilePickerModule,
        ForgeIconButtonModule,
        ForgeIconModule,
        ForgeLabelValueModule,
        ForgeTooltipModule,
        NgClass,
    ]
})
// @LogComponentChanges
export class MetadataInputFileComponent implements AfterViewInit, OnChanges, DoCheck, OnDestroy
{
    modelGroup = inject(NgModelGroup);
    private readonly $fileService = inject(FileService);
    private readonly $metadataPropertyService = inject(MetadataPropertyService);
    private readonly domSanitizer = inject(DomSanitizer);

    // not really readonly, but we should treat it as immutable within this component.
    @Input()
    readonly model: IFile[] | IFile;

    @Input()
    metadataModel: AugmentedModelObject; // model should be a field on metadataModel, FYI

    @Input()
    parent: AugmentedModelObject; // metadataModel should be a field on parent, FYI

    @Input()
    propertyMetadata: PropertyMetadata;

    @Input()
    viewOnly?: boolean;

    @Input()
    compact?: boolean;

    @Input()
    label: string;

    @Output()
    onValueChanged = new EventEmitter<ValueChangedParms>();

    /** Accessible in the template */ isViewOnly: boolean;

    /** Accessible in the template */ documentUrl: SafeResourceUrl;

    private _valueModel: IFile[] | IFile;

    showTypeField: boolean = true;

    private _maxFileSize: string = "52428800"; // = 50MB

    private _badFileList: BadFile[] = [];

    private initialized: boolean = false;

    private _showDocument: boolean = false;
    url: string;

    private readonly showableTypesByExtension: Record<string, string> = {
        ".png": "image/png",
        ".jpg": "image/jpeg",
        ".jpeg": "image/jpeg",
        ".pdf": "application/pdf",
    };

    ngDoCheck(): void
    {
        this.isViewOnly =
            (this.viewOnly && !this.propertyMetadata.ViewOnlyEnabled) ||
            this.$metadataPropertyService.isViewOnly(this.propertyMetadata, this.metadataModel, this.parent) ||
            this.$metadataPropertyService.isBeingRecalculated(this.propertyMetadata, this.metadataModel);
    }

    ngAfterViewInit(): void
    {
        if (this.initialized)
            return;
        this.initialized = true;
        void Promise.resolve().then(() => this.modelGroup?.control?.addControl(this.Name, new FormControl()));
    }

    ngOnChanges(changesObj: SimpleChanges): void
    {
        {
            const changedKeys = Object.keys(changesObj);
            for (const key of changedKeys)
            {
                switch (key)
                {
                    case "model": {
                        const change = changesObj[key];
                        if (!change.currentValue)
                        {
                            this._valueModel = null;
                        }
                        else if (!this.valueModel || this.valueModel !== change.currentValue)
                        {
                            if (Array.isArray(change.currentValue))
                            {
                                const values = [];
                                this._valueModel = [];
                                change.currentValue.forEach(val =>
                                {
                                    values.push(MetadataInputFileComponent.toFileObject(val));
                                });
                                this._valueModel = values;
                            }
                            else
                            {
                                this._valueModel = MetadataInputFileComponent.toFileObject(change.currentValue);
                            }
                        }
                        else if (this.valueModel && this.isShowable && this.propertyMetadata.DefaultShow)
                        {
                            this.show();
                        }
                    }
                }
                break;
            }
        }
    }

    ngOnDestroy(): void
    {
        if (this.url)
            URL.revokeObjectURL(this.url);
    }


    private get valueModel(): IFile[] | IFile
    {
        if (!this._valueModel)
        {
            if (Array.isArray(this._valueModel))
            {
                const values = [];
                for (const val in this.model as any)
                {
                    values.push(MetadataInputFileComponent.toFileObject(this.model[val]));
                }
                this._valueModel = values;
            }
            else
            {
                this._valueModel = MetadataInputFileComponent.toFileObject(this.model as IFile);
            }
        }
        return this._valueModel;
    }

    get file(): IFile
    {
        return !Array.isArray(this.valueModel)
            ? this.valueModel as IFile
            : undefined;
    }

    get files(): IFile[]
    {
        return Array.isArray(this.valueModel)
            ? this.valueModel as IFile[]
            : [];
    }

    // set valueModel(value: IFile[] | IFile)
    // {
    //     this._valueModel = value;
    // }

    get badFileList(): BadFile[]
    {
        return this._badFileList;
    }

    set badFileList(value: BadFile[])
    {
        this._badFileList = value;
    }

    get isRequired(): boolean
    {
        return this.$metadataPropertyService.isRequired(this.propertyMetadata, this.metadataModel, this.parent) && !this.isViewOnly;
    }

    get telemetryLabel(): string
    {
        return "Submission-" + this.label;
    }

    get isInvalid(): boolean
    {
        const target_copy = Object.assign({}, this.metadataModel);
        return (
            this.isRequired &&
            !this.filesExist &&
            !Utilities.IsNullOrUndefined(target_copy.$errors) &&
            !Utilities.IsNullOrUndefined(target_copy.$errors[this.Name])
        );
    }

    get horizontalMode(): boolean
    {
        return !this.allowMultiple && !this.showDragDrop;
    }

    get isShowDocumentMode(): boolean
    {
        return this._showDocument;
    }

    get isDownloadable(): boolean
    {
        return (
            this.filesExist &&
            (Utilities.IsNullOrUndefined(this.propertyMetadata.Downloadable)
                ? false
                : this.propertyMetadata.Downloadable)
        );
    }

    get isShowable(): boolean
    {
        return (
            this.filesExist &&
            !this.propertyMetadata.AllowMultipleFiles &&
            (Utilities.IsNullOrUndefined(this.propertyMetadata.Showable)
                ? false
                : this.propertyMetadata.Showable) &&
            this.isWhiteListedType
        );
    }

    get isWhiteListedType(): boolean
    {
        return !!this.showableContentType;
    }

    get showableContentType(): string
    {
        const file = <IFile>this.valueModel;
        const extension = file.FileName.substring(file.FileName.lastIndexOf("."));
        return this.showableTypesByExtension[extension.toLowerCase()];
    }

    get allowMultiple(): boolean
    {
        return this.propertyMetadata.AllowMultipleFiles;
    }

    get showDragDrop(): boolean
    {
        return this.propertyMetadata.ShowDragAndDrop;
    }

    get filesExist(): boolean
    {
        if (Array.isArray(this.valueModel))
        {
            return !!this.valueModel && !!this.valueModel.find(x => x.Contents !== null && x.Contents !== undefined);
        }
        else
        {
            return !!this.valueModel && !!this.valueModel.Contents;
        }
    }

    get buttonMessage(): string
    {
        return this.allowMultiple ? "Select files" : "Select file";
    }

    get primaryMessage(): string
    {
        return this.allowMultiple ? "Drag files here or" : "Drag file here or";
    }

    get allowedTypes(): string
    {
        const defaultTypes = ".txt, .csv, .xls, .xlsx, .pdf";
        if (Utilities.IsNullOrUndefined(this.propertyMetadata.AllowedTypes)) return defaultTypes.replace(/\s/g, "");
        if (this.propertyMetadata.AllowedTypes === "*")
        {
            this.showTypeField = false;
            return ".*";
        }
        return this.propertyMetadata.AllowedTypes.replace(/\s/g, "");
    }

    get allowedTypesView(): string
    {
        return this.propertyMetadata.AllowedTypes === "*" ? "" : this.propertyMetadata.AllowedTypes;
    }

    get customFormName(): string
    {
        return `metadataInputFileForm_${ObjectHelpers.stripInvalidChars(this.propertyMetadata.Name)}`;
    }

    get maxSize(): string
    {
        return this._maxFileSize;
    }

    get Name(): string
    {
        return this.propertyMetadata.Name;
    }

    /** The `FormControl` registered in `ngAfterViewInit`; null until that registration's microtask has run. */
    private get formControl(): AbstractControl
    {
        return this.modelGroup?.control?.controls[this.Name];
    }

    private static toFileObject(value: string | IFile): IFile
    {
        return typeof value === "string" ? <IFile>{ FileName: value } : value;
    }

    get formatMaxSize(): string
    {
        const bytes = parseInt(this.maxSize);
        let size = "";
        if (bytes >= 1073741824)
        {
            size = (bytes / 1073741824).toFixed(0) + " GB";
        }
        else if (bytes >= 1048576)
        {
            size = (bytes / 1048576).toFixed(0) + " MB";
        }
        else if (bytes >= 1024)
        {
            size = (bytes / 1024).toFixed(0) + " KB";
        }
        else if (bytes > 1)
        {
            size = bytes + " bytes";
        }
        else if (bytes === 1)
        {
            size = bytes + " byte";
        }
        else
        {
            size = "0 bytes";
        }
        return size;
    }

    onFileSelected($event): void
    {
        const self = this;
        const htmlInputFile: any = $event.detail;
        if (htmlInputFile.illegalFiles && htmlInputFile.illegalFiles.length >= 1)
        {
            const badFiles = [];
            htmlInputFile.illegalFiles.forEach((file: File) =>
            {
                let message = "";
                if (file.size > parseInt(this.maxSize))
                {
                    message = "File size is too large";
                    this.formControl?.setErrors({ "File size is too large": true });
                }
                else if (!this.validateFileType(file))
                {
                    message = "Invalid file type";
                    this.formControl?.setErrors({ "Invalid file type": true });
                }
                badFiles.push({
                    FileName: file.name,
                    Message: message,
                });
            });
            this.badFileList = badFiles;
        }
        else
        {
            this.badFileList = [];
        }
        if (!htmlInputFile.legalFiles || htmlInputFile.legalFiles.length < 1)
        {
            return;
        }

        this.formControl?.setErrors(null);
        const readers = [];
        htmlInputFile.legalFiles.forEach(file =>
        {
            readers.push(getFiles(file));
        });

        Promise.all(readers).then((values) =>
        {
            if (self.metadataModel?.$errors)
                delete self.metadataModel.$errors[self.Name];
            this.fireValueChanged(this.valueModel);
            // componentContext.fireValueChanged(componentContext.valueModel);
        });

        function getFiles(file: File): Promise<IFile | Array<IFile>>
        {
            return new Promise(function (resolve, _reject)
            {
                const reader = new FileReader();
                reader.onload = function ()
                {
                    const uInt8Array = new Uint8Array(reader.result as ArrayBuffer);
                    let charCode = "";
                    //Avoiding maximum stack depth
                    for (let i = 0; i < uInt8Array.length; ++i)
                    {
                        charCode += String.fromCharCode(uInt8Array[i]);
                    }

                    const data = btoa(charCode);
                    const newFile: IFile = {
                        Contents: data,
                        FileName: file.name,
                        Type: file.type
                    };
                    if (self.allowMultiple)
                    {
                        if (Utilities.IsNullOrUndefined(self.valueModel))
                        {
                            self._valueModel = [];
                        }
                        self.files.push(newFile);
                    }
                    else
                    {
                        self._valueModel = newFile;
                    }
                    resolve(self.valueModel);
                };
                reader.readAsArrayBuffer(file);
            });

        }
    }

    validateFileType(file: File): boolean
    {
        const extension = file.name.substring(file.name.lastIndexOf("."), file.name.length);
        return this.allowedTypes.includes(extension);
    }

    fireValueChanged(newValue: IFile[] | IFile): void
    {
        if (this.isShowable && this.propertyMetadata.DefaultShow)
        {
            this.show();
        }

        if (this.model === newValue || !this.onValueChanged)
        {
            return;
        }
        this.onValueChanged.emit({ value: newValue });
    }

    download(): void
    {
        if (Array.isArray(this.valueModel))
        {
            this.valueModel.forEach(file =>
            {
                this.$fileService.openDocumentLink(file.Contents, file.Type, file.FileName);
            });
        }
        else
        {
            this.$fileService.openDocumentLink(
                this.valueModel.Contents,
                this.valueModel.Type,
                this.valueModel.FileName
            );
        }
    }

    show(): void
    {
        const contentType = this.showableContentType;
        if (!contentType)
            return;
        if (this.url)
            URL.revokeObjectURL(this.url);
        const file = this.valueModel as IFile;
        const blob = this.$fileService.b64toBlob(file.Contents, contentType, true);
        this.url = URL.createObjectURL(blob);
        this.documentUrl = this.domSanitizer.bypassSecurityTrustResourceUrl(this.url);
        this._showDocument = true;
    }

    hide(): void
    {
        this._showDocument = false;
        URL.revokeObjectURL(this.url);
    }

    clear(file): void
    {
        if (!Utilities.IsNullOrUndefined(file) && Array.isArray(this.valueModel))
        {
            const index = this.valueModel.indexOf(file);
            if (index !== -1)
            {
                this.valueModel.splice(index, 1);
            }
            this.fireValueChanged(this.valueModel);
        }
        else
        {
            this.fireValueChanged(null);
        }
    }
}
